import React, { useState, useEffect, memo } from 'react';
import { Send, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { trackFormSubmission } from '../utils/analytics';

// Declare Firebase types
declare global {
  interface Window {
    firebase: any;
  }
}

// Business email validation helper
const isBusinessEmail = (email: string): boolean => {
  const personalDomains = [
    'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 
    'aol.com', 'icloud.com', 'me.com', 'mac.com', 'live.com',
    'msn.com', 'ymail.com', 'protonmail.com', 'proton.me'
  ];
  
  const emailDomain = email.split('@')[1]?.toLowerCase();
  if (!emailDomain) return false;
  
  return !personalDomains.includes(emailDomain);
};

interface ContactFormProps {
  formSource?: 'contact' | 'demo' | 'footer';
  prefilledInterest?: string;
  sourcePrefix?: string;
}

const ContactForm: React.FC<ContactFormProps> = ({ 
  formSource = 'contact',
  prefilledInterest = ''
}) => {
  const [formData, setFormData] = useState({
    from_name: '',
    from_email: '',
    company: '',
    phone: '',
    interest: prefilledInterest || '',
    message: '',
    source: '',
    form_type: formSource, // Track whether this is contact or demo request
    // Honeypot field - should remain empty
    email_confirm: ''
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState('idle'); // idle, submitting, success, error
  const [formStartTime] = useState(Date.now());

  const interestOptions = formSource === 'demo' 
    ? [
        'Schedule Demo',
        'AI Readiness Assessment',
        'Implementation Roadmap',
        'Change Management',
        'Executive Briefing',
        'General Inquiry'
      ]
    : [
        'AI Readiness Assessment',
        'Implementation Roadmap', 
        'Change Management',
        'Executive Briefing',
        'General Inquiry'
      ];

  const sourceOptions = [
    'Google Search',
    'LinkedIn',
    'Referral',
    'Event/Webinar',
    'Other'
  ];

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.from_name || formData.from_name.length < 2) {
      newErrors.from_name = 'Name must be at least 2 characters';
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.from_email || !emailRegex.test(formData.from_email)) {
      newErrors.from_email = 'Please enter a valid email';
    } else if (!isBusinessEmail(formData.from_email)) {
      newErrors.from_email = 'Please use your business email address';
    }
    
    if (!formData.company) {
      newErrors.company = 'Company name is required';
    }
    
    if (!formData.interest) {
      newErrors.interest = 'Please select your interest';
    }
    
    if (!formData.message || formData.message.length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Security checks
    const timeTaken = Date.now() - formStartTime;
    const minTimeRequired = 3000; // 3 seconds minimum
    
    // Honeypot check - if filled, it's likely a bot
    if (formData.email_confirm && formData.email_confirm.trim() !== '') {
      console.log('Honeypot triggered - potential bot detected');
      // Silently fail - don't give feedback to bot
      setStatus('error');
      return;
    }
    
    // Time-based check - if submitted too quickly, likely a bot
    if (timeTaken < minTimeRequired) {
      console.log(`Form submitted too quickly: ${timeTaken}ms`);
      setStatus('error');
      return;
    }
    
    if (!validateForm()) {
      return;
    }
    
    setStatus('submitting');
    
    try {
      // Check if Firebase is loaded
      if (!window.firebase || !window.firebase.functions) {
        throw new Error('Firebase is not loaded. Please check your connection and try again.');
      }
      
      // Call Firebase Function (exclude honeypot field)
      const submitContactForm = window.firebase.functions().httpsCallable('submitContactForm');
      const { email_confirm, ...dataToSubmit } = formData;
      
      // Map non-standard interests to accepted values for the Firebase function
      let mappedInterest = 'General Inquiry'; // Default to General Inquiry
      
      // Map known interests to accepted enum values
      if (dataToSubmit.interest === 'AI Readiness Assessment' || 
          dataToSubmit.interest === 'Implementation Roadmap' || 
          dataToSubmit.interest === 'Change Management' || 
          dataToSubmit.interest === 'General Inquiry') {
        mappedInterest = dataToSubmit.interest;
      }
      
      // Enhance message based on interest type to preserve original selection
      let enhancedMessage = dataToSubmit.message;
      
      if (dataToSubmit.interest === 'Schedule Demo') {
        enhancedMessage = `[DEMO REQUEST]\n\n${dataToSubmit.message}`;
      } else if (dataToSubmit.interest === 'Executive Briefing') {
        enhancedMessage = `[EXECUTIVE BRIEFING REQUEST]\n\n${dataToSubmit.message}`;
      } else if (dataToSubmit.interest.includes('Package')) {
        enhancedMessage = `[INTERESTED IN: ${dataToSubmit.interest}]\n\n${dataToSubmit.message}`;
      }
      
      const response = await submitContactForm({
        ...dataToSubmit,
        interest: mappedInterest,
        message: enhancedMessage,
        // Add metadata
        submissionTime: timeTaken,
        userAgent: navigator.userAgent,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      });
      
      console.log('Form submitted successfully:', response.data);
      
      // Track successful form submission
      trackFormSubmission('contact_form');
      
      // Store interest before resetting (for showing founder email on Executive Briefing)
      const submittedInterest = dataToSubmit.interest;
      
      setStatus('success');
      // Store the interest in formData so we can access it in the success view
      setFormData(prev => ({ ...prev, interest: submittedInterest }));
      
    } catch (error) {
      console.error('Email send error:', error);
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="max-w-2xl mx-auto p-8 bg-white rounded-2xl shadow-lg">
        <div className="text-center mb-6">
          <div className="mb-4">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Thank You for Reaching Out!</h3>
          <p className="text-gray-600">We've received your inquiry and appreciate your interest in NeuPath Strategies.</p>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-6 mb-6">
          <h4 className="font-semibold text-gray-900 mb-3">What Happens Next?</h4>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start">
              <span className="text-teal-600 mr-2">1.</span>
              <span>You'll receive a confirmation email shortly with resources relevant to your interests.</span>
            </li>
            <li className="flex items-start">
              <span className="text-teal-600 mr-2">2.</span>
              <span>Our team will review your inquiry and respond within 24 business hours.</span>
            </li>
            <li className="flex items-start">
              <span className="text-teal-600 mr-2">3.</span>
              <span>
                {formData.interest === 'Executive Briefing' 
                  ? "We'll prepare a customized executive briefing aligned with your strategic objectives."
                  : formData.interest === 'AI Readiness Assessment' 
                  ? "We'll include preparation materials to help you get started."
                  : "We'll prepare relevant resources and recommendations for your needs."}
              </span>
            </li>
          </ul>
        </div>
        
        {formData.interest === 'Executive Briefing' && (
          <div className="bg-teal-50 border-2 border-teal-200 rounded-lg p-6 mb-6">
            <h4 className="font-semibold text-gray-900 mb-2">For Immediate Executive Assistance:</h4>
            <p className="text-gray-700">
              Given the strategic nature of your inquiry, you may also reach out directly to our founder:
            </p>
            <div className="mt-3">
              <a 
                href="mailto:mike@neupath.ai?subject=Executive Briefing Request"
                className="inline-flex items-center text-teal-600 hover:text-teal-700 font-semibold"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
                mike@neupath.ai
              </a>
              <p className="text-sm text-gray-600 mt-1">
                Mike Alvarez, Founder - NeuPath Strategies
              </p>
            </div>
          </div>
        )}
        
        <div className="text-center">
          <p className="text-gray-600 mb-4">
            While you wait, why not explore our AI readiness resources?
          </p>
          <button 
            onClick={() => window.location.href = '/'}
            className="inline-flex items-center bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-8 bg-white rounded-2xl shadow-lg">
      <h2 className="text-3xl font-bold text-gray-900 mb-2">Let's Start Your AI Journey</h2>
      <p className="text-gray-600 mb-8">Tell us about your transformation goals and we'll create a custom roadmap for your success.</p>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Name Field */}
          <div>
            <label htmlFor="from_name" className="block text-sm font-medium text-gray-700 mb-1">
              Your Name *
            </label>
            <input
              type="text"
              id="from_name"
              name="from_name"
              value={formData.from_name}
              onChange={handleChange}
              className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all ${
                errors.from_name ? 'border-red-500' : 'border-gray-200'
              }`}
              placeholder="John Doe"
            />
            {errors.from_name && (
              <p className="mt-1 text-sm text-red-600">{errors.from_name}</p>
            )}
          </div>

          {/* Email Field */}
          <div>
            <label htmlFor="from_email" className="block text-sm font-medium text-gray-700 mb-1">
              Email Address *
            </label>
            <input
              type="email"
              id="from_email"
              name="from_email"
              value={formData.from_email}
              onChange={handleChange}
              className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all ${
                errors.from_email ? 'border-red-500' : 'border-gray-200'
              }`}
              placeholder="john@company.com"
            />
            {errors.from_email && (
              <p className="mt-1 text-sm text-red-600">{errors.from_email}</p>
            )}
          </div>

          {/* Company Field */}
          <div>
            <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
              Company *
            </label>
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all ${
                errors.company ? 'border-red-500' : 'border-gray-200'
              }`}
              placeholder="Acme Corp"
            />
            {errors.company && (
              <p className="mt-1 text-sm text-red-600">{errors.company}</p>
            )}
          </div>

          {/* Phone Field */}
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
              Phone (Optional)
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
              placeholder="(555) 123-4567"
            />
          </div>
        </div>

        {/* Interest Field */}
        <div>
          <label htmlFor="interest" className="block text-sm font-medium text-gray-700 mb-1">
            I'm Interested In *
          </label>
          <select
            id="interest"
            name="interest"
            value={formData.interest}
            onChange={handleChange}
            className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all ${
              errors.interest ? 'border-red-500' : 'border-gray-200'
            }`}
          >
            <option value="">Select your primary interest</option>
            {interestOptions.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
          {errors.interest && (
            <p className="mt-1 text-sm text-red-600">{errors.interest}</p>
          )}
        </div>

        {/* Message Field */}
        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
            Tell Us About Your Goals *
          </label>
          <textarea
            id="message"
            name="message"
            rows={4}
            value={formData.message}
            onChange={handleChange}
            className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none ${
              errors.message ? 'border-red-500' : 'border-gray-200'
            }`}
            placeholder="Share your current challenges and what you hope to achieve with AI transformation..."
          />
          {errors.message && (
            <p className="mt-1 text-sm text-red-600">{errors.message}</p>
          )}
        </div>

        {/* Source Field */}
        <div>
          <label htmlFor="source" className="block text-sm font-medium text-gray-700 mb-1">
            How Did You Hear About Us?
          </label>
          <select
            id="source"
            name="source"
            value={formData.source}
            onChange={handleChange}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
          >
            <option value="">Select an option</option>
            {sourceOptions.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>

        {/* Honeypot field - hidden from users but visible to bots */}
        <div 
          style={{
            position: 'absolute',
            left: '-9999px',
            width: '1px',
            height: '1px',
            overflow: 'hidden'
          }}
          aria-hidden="true"
        >
          <label htmlFor="email_confirm">
            Please leave this field empty (for spam protection)
          </label>
          <input
            type="text"
            id="email_confirm"
            name="email_confirm"
            value={formData.email_confirm}
            onChange={handleChange}
            tabIndex={-1}
            autoComplete="off"
          />
        </div>

        {/* Error Message */}
        {status === 'error' && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <p className="text-red-600">Something went wrong. Please try again or email us directly.</p>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="w-full bg-gradient-to-r from-teal-600 to-teal-700 text-white py-4 px-6 rounded-lg font-semibold hover:from-teal-700 hover:to-teal-800 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 transition-all duration-200 transform hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
        >
          {status === 'submitting' ? (
            <span className="flex items-center justify-center">
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Sending...
            </span>
          ) : (
            <span className="flex items-center justify-center">
              <Send className="w-5 h-5 mr-2" />
              Send Message
            </span>
          )}
        </button>

        <p className="text-center text-sm text-gray-500 mt-4">
          By submitting this form, you agree to our{' '}
          <a href="#privacy" className="text-teal-600 hover:underline">Privacy Policy</a>.
        </p>
      </form>
    </div>
  );
};

export default memo(ContactForm);